import React from "react";
import { SelectionCard, Typography } from "../../../common";
import MarkdownWrapper from "../../../common/MarkdownWrapper";
import { FeedbackMessage } from "../ui-helpers";
import { KnowledgeCheckContent } from "../../../../types/newtypes";
import { useKnowledgeCheckPreview } from "./hooks/useKnowledgeCheckPreview";

interface KnowledgeCheckPreviewProps {
  content: KnowledgeCheckContent;
  onCompleted?: () => void;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
  cardId: string;
}

const KnowledgeCheckPreview: React.FC<KnowledgeCheckPreviewProps> = ({
  content,
  onCompleted,
  onCardStateChange,
  cardId,
}) => {
  const {
    selectedAnswer,
    isSelectionCorrect,
    feedbackTitle,
    feedbackMessage,
    shuffledOptions,
    handleOptionSelect,
    handleResetAnswers,
  } = useKnowledgeCheckPreview({
    content,
    onCardStateChange,
    cardId,
  });

  const hasAnswered = isSelectionCorrect !== null;

  return (
    <div className="w-full">
      {/* Slide Title */}
      <Typography variant="h4" weight="bold" className="mb-4">
        {content?.slide_title}
      </Typography>

      {/* Intro Text */}
      <Typography variant="p" className="!text-base mb-6">
        {content?.intro_text}
      </Typography>

      {/* Question */}
      <div className="mb-6 mt-6">
        <MarkdownWrapper content={content?.question || ""} />
      </div>

      {/* Options (shuffled) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {shuffledOptions?.map((option, index) => {
          const isSelected = selectedAnswer === index;
          const showCorrect =
            hasAnswered && option?.is_correct && isSelectionCorrect === true;
          const showIncorrect =
            hasAnswered && isSelectionCorrect === false && isSelected;

          return (
            <SelectionCard
              key={index}
              type="default"
              label={option?.text}
              checked={isSelected}
              correct={showCorrect}
              incorrect={showIncorrect}
              disabled={hasAnswered}
              onClick={() => handleOptionSelect(index)}
            />
          );
        })}
      </div>

      {/* Feedback */}
      {hasAnswered && (
        <FeedbackMessage
          isSuccess={isSelectionCorrect === true}
          title={feedbackTitle}
          message={feedbackMessage}
          showTryAgain={isSelectionCorrect === false}
          onTryAgain={handleResetAnswers}
        />
      )}
    </div>
  );
};

export default KnowledgeCheckPreview;
